import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Shield, ChevronRight, CreditCard, Building2, Smartphone } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { createRazorpayCheckout } from "../lib/razorpayOptions";
import PayPalTopUpButton from "../components/PayPalTopUpButton";
import { UpiIntentOverlay } from "../components/UpiIntentOverlay";
import { loadCustomRazorpay, upiIntentMode, intentAppFor, isRealPhone, startUpiIntent } from "../lib/upiIntent";

// Razorpay checkout script — MUST be injected before `new window.Razorpay(...)`
// is called. Every other Razorpay entry point in this app (Store, VIP, Zoom,
// Courses, the legacy WalletTopUp modal) loads this manually. The new
// two-step /wallet/topup/pay page used to skip this step which caused a
// silent `window.Razorpay is not a constructor` throw the first time a
// buyer landed directly on the page (i.e. hadn't already opened any other
// Razorpay-powered page in the same session). Toast surfaced it as
// "couldn't create payment order (HTTP undefined)".
const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Wallet top-up — Step 2: Payment Information (full page).
 *
 * Reads the amount from sessionStorage (set by Step 1), computes GST,
 * lets the user apply a coupon and select a payment method, then opens
 * Razorpay (or the native PayPal button for USD) with the chosen method
 * pre-selected via `config.display.blocks`.
 *
 * For INR customers we surface UPI apps (PhonePe/GPay/Paytm) via
 * intent-flow, other UPI, Cards, generic UPI, Net Banking.
 *
 * For USD customers the same layout renders PayPal (native SDK button),
 * International Cards (Razorpay), and Google Pay International.
 */

const METHOD = {
  PHONEPE:   "phonepe",
  GPAY:      "gpay",
  PAYTM:     "paytm",
  OTHER_UPI: "other_upi",
  CARD:      "card",
  UPI:       "upi_collect",
  NETBANKING:"netbanking",
  UPI_ALL:   "all_methods",
  // USD-only
  PAYPAL:    "paypal",
  INTL_CARD: "intl_card",
  GPAY_INTL: "gpay_intl",
};

export default function WalletTopUpPayPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, checkAuth } = useAuth();
  const { showingUsd, usd_to_inr } = useCurrency();

  // Amount comes from Step 1 via sessionStorage.
  const inrAmount = Number(sessionStorage.getItem("wallet_topup_amount_inr") || 0);
  const currency = sessionStorage.getItem("wallet_topup_currency") || (showingUsd ? "USD" : "INR");

  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [method, setMethod] = useState(showingUsd ? METHOD.PAYPAL : METHOD.PHONEPE);
  const [paying, setPaying] = useState(false);
  // Auto-offer state — mirrors the backend's `_bonus_for_amount` tier math
  // so we can show `ASTROFFER5/10/12/15/20 applied` immediately without a
  // round-trip. `autoOfferDismissed` lets buyers REMOVE the badge (the
  // bonus stays server-side either way — this is a UI dismiss only).
  const [autoOfferDismissed, setAutoOfferDismissed] = useState(false);
  const [celebrateShown, setCelebrateShown] = useState(false);
  // Sticky "already shown" flag. Without this, the useEffect that opens
  // the modal fires again the moment `celebrateShown` flips back to
  // false, trapping the user in an unclosable popup. Ref survives
  // re-renders without triggering them.
  const celebrateFiredRef = React.useRef(false);

  const autoOffer = useMemo(() => {
    // Must match `_bonus_for_amount` in server.py — which walks
    // TOPUP_PACKS (₹250/₹500/₹1000/₹2000 tiers) and returns the highest
    // pack's bonus that the amount qualifies for. If we drift from this,
    // the celebration card will promise the wrong bonus.
    if (inrAmount < 250) return null;
    let bonusInr = 25;                // ₹250 pack
    if (inrAmount >= 2000) bonusInr = 500;
    else if (inrAmount >= 1000) bonusInr = 200;
    else if (inrAmount >= 500)  bonusInr = 75;
    const pct = Math.round((bonusInr / inrAmount) * 100);
    return { code: `ASTROFFER${pct}`, pct, bonusInr };
  }, [inrAmount]);

  // Fire the "Woohoo!" celebration modal the first time an auto-offer
  // becomes visible on this page load. `celebrateFiredRef` is a one-way
  // latch so dismissing the modal doesn't retrigger it.
  useEffect(() => {
    if (celebrateFiredRef.current) return;
    if (autoOffer && !autoOfferDismissed && !appliedCoupon) {
      celebrateFiredRef.current = true;
      setCelebrateShown(true);
    }
  }, [autoOffer, autoOfferDismissed, appliedCoupon]);

  const rate = showingUsd && usd_to_inr ? usd_to_inr : 1;
  const symbol = showingUsd ? "$" : "₹";
  const gstRate = 0.18;
  const gstInr = Math.round(inrAmount * gstRate * 100) / 100;
  const totalInr = Math.round((inrAmount + gstInr) * 100) / 100;
  const displaySub = showingUsd ? +(inrAmount / rate).toFixed(2) : inrAmount;
  const displayGst = showingUsd ? +(gstInr / rate).toFixed(2) : gstInr;
  const displayTotal = showingUsd ? +(totalInr / rate).toFixed(2) : totalInr;

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/wallet/topup"); return; }
    if (!inrAmount) { navigate("/wallet/topup", { replace: true }); }
  }, [user, authLoading, inrAmount, navigate]);

  // Preload the Razorpay checkout script on mount so that clicking
  // "Proceed to pay" opens the popup instantly (and never fails with
  // "window.Razorpay is not a constructor" on a cold load).
  // UPI Intent (direct app opening) — Android app + phone browsers, INR only.
  const intentMode = useMemo(() => upiIntentMode(), []);
  const intentApp = !showingUsd ? intentAppFor(method, intentMode) : null;
  const couponKey = appliedCoupon?.code || "";
  const [customCtor, setCustomCtor] = useState(null);
  const [preOrder, setPreOrder] = useState(null);
  const [upiPhone, setUpiPhone] = useState("");
  const [waitingApp, setWaitingApp] = useState(null);
  const intentRef = React.useRef(null);

  useEffect(() => {
    loadRazorpayScript().then((ok) => {
      if (ok && intentMode) loadCustomRazorpay().then((C) => setCustomCtor(() => C));
    });
  }, [intentMode]);

  const createOrder = () => api.post("/wallet/razorpay/order", {
    amount: inrAmount,
    coupon_code: appliedCoupon?.code || null,
    display_currency: showingUsd ? "USD" : "INR",
  }).then((r) => r.data);

  // Pre-create the order so the UPI app opens straight from the tap
  // (Razorpay: no long async work between the click and createPayment).
  useEffect(() => {
    if (!intentApp || !user || !inrAmount || preOrder?.key === couponKey) return;
    let alive = true;
    createOrder().then((data) => alive && setPreOrder({ key: couponKey, data })).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentApp, couponKey, user, inrAmount]);

  const savedPhone = preOrder?.data?.prefill?.contact;
  const needsPhone = !!intentApp && !!preOrder && !isRealPhone(savedPhone);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/wallet/coupons/validate", { code: coupon.trim().toUpperCase(), amount: inrAmount });
      if (data.valid) {
        setAppliedCoupon({ code: coupon.trim().toUpperCase(), bonus: data.bonus, description: data.description });
        toast.success(`Coupon applied — +₹${data.bonus} bonus!`);
      } else {
        toast.error(data.reason || "Invalid coupon code.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't validate coupon.");
    } finally { setCouponBusy(false); }
  };

  // Map our method choice → Razorpay display block override.
  // Returns { config: {...} } patch merged into the base Razorpay options.
  //
  // Two-step UX bug (Mar 2026 report): tapping our PhonePe/GPay/Paytm
  // tile used to open Razorpay on a "Pay via PhonePe → UPI" category
  // screen (Step 1) and only after tapping "UPI" would it reveal the
  // actual app tile (Step 2). Reason: passing `{ method: "upi", apps: [...] }`
  // as an *instrument* renders it as a UPI CATEGORY block that has to be
  // expanded. To skip Step 1 and land the buyer directly on the app-select
  // screen (or trigger the deep-link on mobile), we now also set the
  // undocumented but Razorpay-supported `_` (private) options bag that
  // forces the checkout to open in intent-flow with the app pre-selected.
  //   • `_.flow = "intent"`      → skip UPI category / collect landing
  //   • `_.upi_app = "phonepe"`  → pre-select the tapped app on the intent screen
  //   • `prefill.method = "upi"` → tells checkout to pick UPI without any tap
  // Combined with the display-block whitelist below (which already hides
  // every other rail), this makes the popup boot straight into the
  // one-tap UPI screen. True one-tap (no popup at all) requires the S2S
  // UPI Intent API — see /app/memory/RAZORPAY_UPI_S2S_INTENT_REQUEST.md.
  const razorpayMethodOverride = (m) => {
    // For INR flows: force Razorpay to open with ONLY the chosen method's block.
    if (m === METHOD.PHONEPE || m === METHOD.GPAY || m === METHOD.PAYTM || m === METHOD.OTHER_UPI) {
      // UPI intent-flow: hint the app slug so Razorpay pre-selects it in the UPI screen.
      const appHint = { [METHOD.PHONEPE]: "phonepe", [METHOD.GPAY]: "google_pay", [METHOD.PAYTM]: "paytm" }[m];
      return {
        method: { upi: true, card: false, netbanking: false, wallet: false, paylater: false, emi: false },
        prefill: { method: "upi" },
        _: appHint ? { flow: "intent", upi_app: appHint } : { flow: "intent" },
        config: { display: { blocks: {
          upi_block: {
            name: appHint ? `Pay via ${appHint === "phonepe" ? "PhonePe" : appHint === "google_pay" ? "Google Pay" : "Paytm"}` : "Pay with any UPI app",
            instruments: appHint
              ? [{ method: "upi", flows: ["intent"], apps: [appHint] }]
              : [{ method: "upi", flows: ["intent", "collect"] }],
          },
        }, sequence: ["block.upi_block"], preferences: { show_default_blocks: false } } },
      };
    }
    if (m === METHOD.CARD) {
      return {
        method: { card: true, upi: false, netbanking: false, wallet: false, paylater: false, emi: false },
        prefill: { method: "card" },
        config: { display: { blocks: { card_only: { name: "Pay with Credit/Debit Card", instruments: [{ method: "card" }] } },
          sequence: ["block.card_only"], preferences: { show_default_blocks: false } } },
      };
    }
    if (m === METHOD.UPI) {
      return {
        method: { upi: true, card: false, netbanking: false, wallet: false, paylater: false, emi: false },
        prefill: { method: "upi" },
        config: { display: { blocks: { upi_only: { name: "Pay via UPI", instruments: [{ method: "upi" }] } },
          sequence: ["block.upi_only"], preferences: { show_default_blocks: false } } },
      };
    }
    if (m === METHOD.NETBANKING) {
      return {
        method: { netbanking: true, card: false, upi: false, wallet: false, paylater: false, emi: false },
        prefill: { method: "netbanking" },
        config: { display: { blocks: { nb_only: { name: "Pay via Net Banking", instruments: [{ method: "netbanking" }] } },
          sequence: ["block.nb_only"], preferences: { show_default_blocks: false } } },
      };
    }
    // USD: International Cards
    if (m === METHOD.INTL_CARD) {
      return {
        method: { card: true, upi: false, netbanking: false, wallet: false, paylater: false, emi: false },
        prefill: { method: "card" },
        config: { display: { blocks: { intl_card: { name: "Pay with International Card", instruments: [{ method: "card" }] } },
          sequence: ["block.intl_card"], preferences: { show_default_blocks: false } } },
      };
    }
    // USD: Google Pay International
    if (m === METHOD.GPAY_INTL) {
      return {
        method: { wallet: true, card: false, upi: false, netbanking: false, paylater: false, emi: false },
        prefill: { method: "wallet" },
        _: { flow: "intent", wallet: "googlepay" },
        config: { display: { blocks: { gpay_intl: { name: "Pay with Google Pay", instruments: [{ method: "wallet", wallets: ["googlepay"] }] } },
          sequence: ["block.gpay_intl"], preferences: { show_default_blocks: false } } },
      };
    }
    return {};
  };

  const verifyAndFinish = async (rzpResp) => {
    try {
      const { data: v } = await api.post("/wallet/razorpay/verify", {
        razorpay_order_id: rzpResp.razorpay_order_id,
        razorpay_payment_id: rzpResp.razorpay_payment_id,
        razorpay_signature: rzpResp.razorpay_signature,
        coupon_code: appliedCoupon?.code || null,
      });
      toast.success(`Wallet topped up! Balance: ₹${v.balance}`);
      await checkAuth?.();
      sessionStorage.removeItem("wallet_topup_amount_inr");
      sessionStorage.removeItem("wallet_topup_currency");
      navigate("/wallet");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment verification failed. If money was deducted, it'll auto-credit shortly.");
    } finally { setPaying(false); }
  };

  const openStandardCheckout = (data, chosenMethod) => {
    // Build checkout options with the CHOSEN method's block override
    const override = razorpayMethodOverride(chosenMethod);
    // Merge server prefill (name/email/contact) with the override's
    // `prefill.method` hint. Server always fills `contact` — either the
    // real saved phone (E.164) or a country-code stub ("+91" / "+1"). We
    // detect the stub so we only lock the field on repeat buyers.
    const mergedPrefill = { ...data.prefill, ...(override.prefill || {}) };
    const serverContact = String(data.prefill?.contact || "").trim();
    const hasRealSavedPhone = serverContact && serverContact !== "+91" && serverContact !== "+1";
    const hasRealSavedEmail = !!(data.prefill?.email && String(data.prefill.email).includes("@"));
    const options = {
      key: data.key_id,
      amount: data.amount_paise,
      currency: data.currency,
      order_id: data.order_id,
      name: "AstroKuberChat",
      description: `Wallet Top-up · ${symbol}${displayTotal}`,
      prefill: mergedPrefill,
      // Only lock contact/email when we ACTUALLY have them saved.
      readonly: {
        ...(hasRealSavedEmail ? { email: true } : {}),
        ...(hasRealSavedPhone ? { contact: true } : {}),
      },
      remember_customer: false,
      theme: { color: "#F0D65B" },
      modal: { ondismiss: () => setPaying(false) },
      ...override,
      // Re-apply the merged prefill AFTER the override spread.
      prefill: mergedPrefill,
      handler: verifyAndFinish,
    };
    const rzp = createRazorpayCheckout(options);
    rzp.on?.("payment.failed", () => { setPaying(false); toast.error("Payment failed. Please try again."); });
    rzp.open();
  };

  // Fallback used when the UPI app can't open: full Razorpay checkout, same order.
  const fallbackToStandard = (data) => {
    setWaitingApp(null);
    intentRef.current = null;
    const typed = upiPhone.length === 10 && !isRealPhone(data.prefill?.contact) ? `+91${upiPhone}` : null;
    openStandardCheckout(typed ? { ...data, prefill: { ...data.prefill, contact: typed } } : data, METHOD.UPI_ALL);
  };

  const tryUpiIntent = () => {
    if (!intentApp || !customCtor || !preOrder || preOrder.key !== couponKey) return false;
    const data = preOrder.data;
    const email = data.prefill?.email;
    const contact = isRealPhone(savedPhone) ? savedPhone : (upiPhone.length === 10 ? `+91${upiPhone}` : null);
    if (!email || !contact) return false;
    try {
      const token = {};
      intentRef.current = token;
      const inst = startUpiIntent(customCtor, {
        order: data,
        app: intentApp,
        contact,
        email,
        webview: intentMode === "android-webview",
        onSuccess: (resp) => { setWaitingApp(null); intentRef.current = null; verifyAndFinish(resp); },
        onError: (resp) => {
          if (!intentRef.current) return; // user already cancelled / switched
          const reason = resp?.error?.reason;
          if (reason === "payment_cancelled") {
            setWaitingApp(null); intentRef.current = null; setPaying(false);
            toast.error("Payment cancelled. You can try again.");
            return;
          }
          fallbackToStandard(data);
        },
      });
      if (intentRef.current !== token) return true; // errored synchronously → fallback already open
      intentRef.current = inst;
      setWaitingApp(intentApp);
      return true;
    } catch (err) {
      console.warn("[upi-intent] createPayment failed, using standard checkout", err);
      fallbackToStandard(data);
      return true;
    }
  };

  const cancelUpiIntent = () => {
    const inst = intentRef.current;
    intentRef.current = null;
    try { inst?.emit?.("payment.cancel"); } catch (e) { /* already closed */ }
    setWaitingApp(null);
    setPaying(false);
  };

  const proceedToPay = async () => {
    if (paying) return;
    if (needsPhone && upiPhone.length !== 10) {
      toast.error("Enter your 10-digit mobile number to pay with UPI.");
      return;
    }
    setPaying(true);
    if (tryUpiIntent()) return;
    try {
      // Ensure Razorpay checkout.js is available before we build any options.
      const rzpReady = await loadRazorpayScript();
      if (!rzpReady || !window.Razorpay) {
        throw new Error("Payment gateway couldn't load. Check your connection and retry.");
      }
      const data = await createOrder();
      openStandardCheckout(data, method);
    } catch (err) {
      setPaying(false);
      // Surface the actual server error to the toast when available —
      // "Couldn't create payment order" alone hides useful signal (was
      // hiding a specific 400 detail from the buyer during Mar 2026 QA).
      const serverDetail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const netMsg = err?.message;
      console.error("[topup] order create failed", { status, serverDetail, netMsg, err });
      if (serverDetail) toast.error(String(serverDetail));
      else if (status) toast.error(`Couldn't create payment order (HTTP ${status}). Please retry.`);
      else toast.error(`Network error: ${netMsg || "check your connection"}.`);
    }
  };

  const upiApps = [
    { key: METHOD.PHONEPE, name: "PhonePe", logo: "/upi/phonepe.png" },
    { key: METHOD.GPAY,    name: "GPay",    logo: "/upi/gpay.png" },
    { key: METHOD.PAYTM,   name: "Paytm",   logo: "/upi/paytm.png" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="wallet-topup-pay-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition"
          data-testid="pay-back-btn"
        >
          <ArrowLeft size={18} className="text-slate-700" />
        </button>
        <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Payment Information</h1>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-5 pb-32 space-y-4">
        {/* Summary card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="pay-summary-card">
          <div className="flex justify-between text-slate-700 text-[15px]"><span>Recharge Amount</span><span>{symbol} {displaySub}</span></div>
          <div className="flex justify-between text-slate-700 text-[15px] mt-2">
            <span>{showingUsd ? "Taxes (18%)" : "GST(18%)"}</span><span>{symbol} {displayGst}</span>
          </div>
          <div className="flex justify-between text-slate-900 text-[15px] mt-2 font-semibold">
            <span>Total Recharge Amount</span><span>{symbol} {displayTotal}</span>
          </div>
          <div className="h-px bg-slate-100 my-3" />
          <div className="text-slate-500 text-sm">
            Billing State: <span className="text-slate-800 border-b border-dashed border-slate-400">{user?.state || "India"}</span>
            <p className="text-slate-400 text-xs mt-1">By continuing, I warrant that this location information is accurate.</p>
          </div>
          <div className="mt-3 flex items-center gap-2 text-emerald-700 text-sm">
            <Shield size={16} /> <span>100% Safe and Secure</span>
          </div>
        </div>

        {/* Coupon area — auto-offer green card takes precedence over the
            manual coupon input. Auto-offer amount is already going to be
            credited server-side (via `_bonus_for_amount`); this card is a
            visual acknowledgement + REMOVE affordance. */}
        {autoOffer && !autoOfferDismissed && !appliedCoupon ? (
          <div
            className="rounded-2xl border border-emerald-100 bg-emerald-50/50 shadow-sm overflow-hidden"
            data-testid="pay-auto-offer-card"
          >
            <div className="px-5 pt-4 pb-2 text-emerald-700 font-semibold text-[15px] tracking-tight">
              <span className="font-mono">`{autoOffer.code}`</span> applied
            </div>
            <div className="flex items-center justify-between gap-3 px-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs shrink-0">✓</div>
                <p className="text-slate-800 font-semibold text-[15px] leading-snug">
                  {showingUsd
                    ? `$${(autoOffer.bonusInr / (rate || 1)).toFixed(2)} extra`
                    : `₹${autoOffer.bonusInr} extra`} in your wallet with this recharge
                </p>
              </div>
              <button
                onClick={() => setAutoOfferDismissed(true)}
                className="text-slate-500 text-xs font-semibold tracking-wider hover:text-slate-700 shrink-0"
                data-testid="pay-auto-offer-remove-btn"
              >
                REMOVE
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-[#F1F9F4] to-white rounded-2xl p-5 shadow-sm" data-testid="pay-coupon-card">
            <p className="text-emerald-800 font-semibold text-base">Apply Coupon</p>
            <div className="mt-3 flex items-stretch gap-0 border border-slate-200 rounded-xl overflow-hidden bg-white">
              <input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                placeholder="Enter Coupon Code"
                className="flex-1 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none"
                data-testid="pay-coupon-input"
                disabled={!!appliedCoupon}
              />
              <button
                onClick={applyCoupon}
                disabled={couponBusy || !coupon.trim() || !!appliedCoupon}
                className="px-5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 font-semibold text-sm tracking-wider"
                data-testid="pay-coupon-apply-btn"
              >
                {appliedCoupon ? "APPLIED" : "APPLY"}
              </button>
            </div>
            {appliedCoupon && (
              <p className="mt-2 text-emerald-700 text-xs">
                ✓ {appliedCoupon.code} — +₹{appliedCoupon.bonus} bonus applied
              </p>
            )}
          </div>
        )}

        {/* Method selection — INR customer view */}
        {!showingUsd && (
          <>
            <p className="text-slate-500 text-sm px-1 pt-2">Pay via UPI</p>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="grid grid-cols-3 gap-3">
                {upiApps.map((app) => {
                  const active = method === app.key;
                  return (
                    <button
                      key={app.key}
                      onClick={() => setMethod(app.key)}
                      className={`relative rounded-xl border p-3 flex flex-col items-center justify-center transition ${
                        active ? "border-blue-500 bg-blue-50/40 shadow-sm" : "border-slate-100 hover:border-slate-200"
                      }`}
                      style={{ minHeight: 96 }}
                      data-testid={`pay-method-${app.key}`}
                    >
                      {active && (
                        <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-white">
                        <img src={app.logo} alt={app.name} className="w-full h-full object-contain" data-testid={`upi-logo-${app.key}`} />
                      </div>
                      <p className="text-slate-700 text-sm font-medium mt-2">{app.name}</p>
                    </button>
                  );
                })}
              </div>
              <div className="h-px bg-slate-100 my-3" />
              <button
                onClick={() => { setMethod(METHOD.OTHER_UPI); }}
                className={`w-full flex items-center justify-between py-2 px-1 ${method === METHOD.OTHER_UPI ? "text-blue-600" : "text-slate-700"}`}
                data-testid="pay-method-other-upi"
              >
                <span className="flex items-center gap-3">
                  <Smartphone size={18} className="text-slate-400" />
                  <span className="text-sm font-medium">Pay with other UPI apps</span>
                </span>
                <ChevronRight size={18} className="text-slate-400" />
              </button>
              {needsPhone && (
                <div className="mt-3" data-testid="upi-phone-field">
                  <label className="text-slate-500 text-xs" htmlFor="upi-phone-input">Mobile number (needed once for UPI)</label>
                  <div className="mt-1 flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white">
                    <span className="px-3 text-slate-500 text-sm">+91</span>
                    <input
                      id="upi-phone-input"
                      inputMode="numeric"
                      value={upiPhone}
                      onChange={(e) => setUpiPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="10-digit mobile number"
                      className="flex-1 py-3 pr-3 text-slate-800 placeholder:text-slate-400 outline-none"
                      data-testid="upi-phone-input"
                    />
                  </div>
                </div>
              )}
            </div>

            <p className="text-slate-500 text-sm px-1 pt-2">Cards</p>
            <RadioMethodRow
              testId="pay-method-card"
              active={method === METHOD.CARD}
              onClick={() => setMethod(METHOD.CARD)}
              icon={<CreditCard size={20} className="text-blue-500" />}
              label="Credit/Debit Card"
            />

            <p className="text-slate-500 text-sm px-1 pt-2">Others</p>
            <div className="space-y-2">
              <RadioMethodRow
                testId="pay-method-upi-generic"
                active={method === METHOD.UPI}
                onClick={() => setMethod(METHOD.UPI)}
                icon={<div className="w-6 h-6 rounded bg-orange-100 text-orange-700 text-[9px] font-bold flex items-center justify-center">UPI</div>}
                label="UPI"
              />
              <RadioMethodRow
                testId="pay-method-netbanking"
                active={method === METHOD.NETBANKING}
                onClick={() => setMethod(METHOD.NETBANKING)}
                icon={<Building2 size={20} className="text-slate-500" />}
                label="Net Banking"
              />
            </div>
          </>
        )}

        {/* Method selection — USD customer view */}
        {showingUsd && (
          <>
            <p className="text-slate-500 text-sm px-1 pt-2">Pay with PayPal</p>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <PayPalTopUpButton
                amount={inrAmount}
                couponCode={appliedCoupon?.code || null}
                disabled={paying}
                onSuccess={async () => {
                  await checkAuth?.();
                  sessionStorage.removeItem("wallet_topup_amount_inr");
                  sessionStorage.removeItem("wallet_topup_currency");
                  navigate("/wallet");
                }}
              />
              <p className="text-slate-500 text-[11px] text-center mt-2">
                Venmo &amp; Pay-in-4 supported for eligible US buyers. Wallet credited in ₹ at today&apos;s FX rate.
              </p>
            </div>

            <p className="text-slate-500 text-sm px-1 pt-2">Cards</p>
            <RadioMethodRow
              testId="pay-method-intl-card"
              active={method === METHOD.INTL_CARD}
              onClick={() => setMethod(METHOD.INTL_CARD)}
              icon={<CreditCard size={20} className="text-blue-500" />}
              label="International Card (Visa / MC / Amex)"
            />

            <p className="text-slate-500 text-sm px-1 pt-2">Others</p>
            <RadioMethodRow
              testId="pay-method-gpay-intl"
              active={method === METHOD.GPAY_INTL}
              onClick={() => setMethod(METHOD.GPAY_INTL)}
              icon={<div className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-slate-700">G</div>}
              label="Google Pay"
            />
          </>
        )}
      </main>

      {/* Proceed to Pay CTA — hidden when USD/PayPal method selected (PayPal has its own button) */}
      {!(showingUsd && method === METHOD.PAYPAL) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-5 py-4 z-40" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
          <div className="max-w-2xl mx-auto">
            <button
              onClick={proceedToPay}
              disabled={paying}
              className="w-full bg-[#F0D65B] hover:bg-[#E5C947] disabled:opacity-60 text-slate-900 font-semibold py-4 rounded-2xl transition"
              data-testid="pay-proceed-btn"
            >
              {paying ? "Processing…" : "Proceed to pay"}
            </button>
          </div>
        </div>
      )}

      {waitingApp && (
        <UpiIntentOverlay
          app={waitingApp}
          onCancel={cancelUpiIntent}
          onOtherOptions={() => { const d = preOrder?.data; cancelUpiIntent(); if (d) { setPaying(true); fallbackToStandard(d); } }}
        />
      )}

      {/* Woohoo! celebration modal — auto-shows once when an auto-offer
          triggers on this page. Buyer can dismiss via the X button, the
          Woohoo CTA, or by tapping anywhere on the dimmed backdrop. */}
      {celebrateShown && autoOffer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: "rgba(15, 23, 42, 0.45)" }}
          onClick={() => setCelebrateShown(false)}
          data-testid="pay-offer-celebration"
        >
          {/* Confetti decorations behind the card */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <span className="absolute top-[18%] left-[8%] text-4xl animate-bounce" style={{ animationDuration: "2.5s" }}>🎊</span>
            <span className="absolute top-[24%] right-[12%] text-3xl animate-bounce" style={{ animationDuration: "3s" }}>⭐</span>
            <span className="absolute bottom-[22%] left-[14%] text-3xl animate-bounce" style={{ animationDuration: "2.8s" }}>🎉</span>
            <span className="absolute bottom-[28%] right-[8%] text-4xl animate-bounce" style={{ animationDuration: "2.3s" }}>✨</span>
          </div>
          <div
            className="relative bg-white rounded-3xl w-full max-w-sm px-8 py-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setCelebrateShown(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition"
              aria-label="Close celebration"
              data-testid="pay-offer-celebration-close"
            >
              ✕
            </button>
            <div className="text-center text-5xl">🎉</div>
            <p className="text-center text-slate-500 text-sm mt-4">
              <span className="font-mono">`{autoOffer.code}`</span> applied
            </p>
            <p className="text-center text-slate-900 text-xl font-bold mt-2 leading-snug">
              {showingUsd
                ? `$${(autoOffer.bonusInr / (rate || 1)).toFixed(2)} extra`
                : `₹${autoOffer.bonusInr} extra`} in your wallet<br />with this coupon
            </p>
            <button
              onClick={() => setCelebrateShown(false)}
              className="mt-6 w-full text-orange-500 hover:text-orange-600 text-lg font-semibold transition"
              data-testid="pay-offer-celebration-cta"
            >
              Woohoo!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RadioMethodRow({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      className={`w-full bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between transition ${active ? "ring-2 ring-blue-500 ring-offset-0" : "hover:bg-slate-50"}`}
      data-testid={testId}
    >
      <span className="flex items-center gap-3">
        {icon}
        <span className="text-slate-800 text-[15px] font-medium">{label}</span>
      </span>
      <div className={`w-5 h-5 rounded-full border-2 ${active ? "border-blue-500" : "border-slate-300"} flex items-center justify-center`}>
        {active && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
      </div>
    </button>
  );
}
